import algoliasearch from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import { connectAutocomplete } from 'instantsearch.js/es/connectors';
import connectSearchBox from 'instantsearch.js/es/connectors/search-box/connectSearchBox';
import { history } from 'instantsearch.js/es/lib/routers';
import {
  hits,
  queryRuleCustomData,
  pagination,
  configure,
  stats,
} from 'instantsearch.js/es/widgets';

import { renderAutocomplete } from '@/src/utilities/search/searchRenderFunctions';
import { UiState } from '@/src/utilities/search/types';
import { cleanQueryString } from '@/src/utilities/search/utils';

export function loadConditionSearch(): void {
  const config = window['condition-search'];

  if (!config) {
    return;
  }

  const { searchIndexName, applicationId, searchKey, searchContext } = config;

  const { searchTerm: query } = searchContext;

  const algoliaClient = algoliasearch(applicationId, searchKey);

  const searchClient = {
    ...algoliaClient,
    search(requests: any[]) {
      return algoliaClient.search(requests);
    },
  };

  const search = instantsearch({
    indexName: searchIndexName,
    initialUiState: {
      [searchIndexName]: {
        query,
      },
    },
    searchClient,
    insights: true,
    routing: {
      router: history({
        createURL({ qsModule, routeState, location }) {
          const urlParts = location.href.match(/^(.*?)\/search/);
          const baseUrl = `${urlParts ? urlParts[1] : ''}/`;

          type QueryParams = {
            term?: string;
          };

          const queryParameters = {} as QueryParams;

          // set the query
          if (routeState['term']) {
            queryParameters.term = routeState['term'] as string;
          }

          const queryString = qsModule.stringify(queryParameters, {
            addQueryPrefix: true,
            arrayFormat: 'repeat',
          });

          return `${baseUrl}search${cleanQueryString(queryString)}`;
        },

        parseURL({ qsModule, location }): UiState {
          const { query = '' } = qsModule.parse(location.search.slice(1));

          return {
            term: decodeURIComponent(query as string),
          };
        },
      }),

      stateMapping: {
        stateToRoute(uiState: UiState): UiState {
          const indexUiState = uiState[searchIndexName];
          return {
            term: indexUiState?.query,
          };
        },
        routeToState(routeState: UiState): UiState {
          return {
            [searchIndexName]: {
              query: routeState['term'],
            },
          };
        },
      },
    },
  });

  const customAutocomplete = connectAutocomplete(renderAutocomplete);

  const virtualSearchBox = connectSearchBox(() => null);

  // utils

  search.addWidgets([
    configure({
      filters: 'ClassName:"URMC.ConditionPage"',
    }),
    queryRuleCustomData({
      container: document.querySelector('[data-app-search-box]'),
      templates: {
        default: '',
      },
      transformItems(items) {
        const match = items.find((data) => Boolean(data.redirect));
        if (match && match.redirect) {
          window.location.href = match.redirect;
        }
        return [];
      },
    }),
    customAutocomplete({
      container: document.querySelector('[data-app-search-box]'),
    }),
    virtualSearchBox({}),

    hits({
      container: '[data-app-search-hits]',
      cssClasses: {
        list: 'list-none p-0',
        item: 'result-list-item',
        root: 'mt-10',
      },
      templates: {
        empty(results, { html }) {
          togglePagination();
          return html`
            <p class="text-body-lg mt-14">
              Try browsing our
              <span> </span>
              <a href="/conditions-and-treatments">condition categories</a>
              <span> </span>
              to find what you need.
            </p>
          `;
        },
        item(hit, { html, components }) {
          const conditionSearchPath = encodeURIComponent(hit['Title']);

          const contentType =
            hit['ContentType'] === 'Department'
              ? 'relatedDepartments'
              : 'relatedConditions';

          const createProviderButton = html`<div>
            <a
              class="font-bold link text-body-regular"
              href="/people/search?${contentType}=${conditionSearchPath}"
            >
              <span>Find Providers for </span>
              ${components.Highlight({ attribute: 'Title', hit })}
            </a>
          </div>`;

          const providerButton = hit['HasRelatedProviders']
            ? createProviderButton
            : '';

          return html`
            <p class="text-sm uppercase text-gray-800/80 mb-1.5 font-semibold">
              ${components.Highlight({ attribute: 'ContentType', hit })}
            </p>
            <h3 class="mt-2 mb-5">
              ${components.Highlight({ attribute: 'Title', hit })}
            </h3>
            <div class="gap-5 flex flex-col sm:gap-10 sm:flex-row">
              <div>
                <a
                  class="font-bold link text-body-regular"
                  href="${hit['Url']}"
                >
                  About ${components.Highlight({ attribute: 'Title', hit })}
                </a>
              </div>
              ${providerButton}
            </div>
          `;
        },
      },
    }),

    stats({
      container: '[data-app-search-stats]',
      templates: {
        text(data, { html }) {
          let count = '';

          if (search.status === 'error') {
            return html`
              <div>
                <p class="text-body-regular">Search is offline</p>
                <p>
                  Sorry, it appears our search tool is currently offline or
                  being worked on. The issue should be resolved soon.
                </p>
              </div>
            `;
          } else if (data.hasManyResults) {
            count += `${data.nbHits} Conditions & Treatments`;
          } else if (data.hasOneResult) {
            count += `1 Condition or Treatment`;
          } else {
            count += `Sorry, we couldn't find any results`;
            return html`
              <p class="text-body-regular">
                <strong>${count} for: "${data.query}"</strong>
              </p>
            `;
          }

          if (data.query) {
            return html` <p>
              <strong>${count}</strong> for
              <strong> "${data.query}"</strong>
            </p>`;
          } else {
            return html` <p>
              <strong>${count}</strong>
            </p>`;
          }
        },
      },
    }),

    pagination({
      container: '[data-app-search-pagination]',
      scrollTo: '[data-app-search-stats]',
      showFirst: true,
      showLast: true,
      padding: 2,
      cssClasses: {
        list: 'justify-center mt-8 remove-space list-none custom-pagination',
        item: 'w-8 h-8 flex items-center justify-center overflow-hidden rounded-sm',
        link: 'border-none',
      },
    }),
  ]);

  search.start();

  // error handler to prevent uncaught errors
  search.on('error', () => {
    console.log('error', search.error);
  });

  search.on('render', () => {
    console.log('search status:', search.status);
    console.log('error', search.status === 'error' && search.error);
  });

  function togglePagination() {
    const pagination = document.querySelector('[data-app-search-pagination]');
    if (!(pagination instanceof HTMLDivElement)) {
      return;
    }
    if (pagination.style.display === 'none') {
      pagination.style.display = 'block';
    } else {
      pagination.style.display = 'none';
    }
  }
}
